/**
 * **What every panel write has in common** — the envelope, the route, the key and the confirm
 * (issue #310).
 *
 * There were two copies of this by #303 and this issue would have made three: `memory.ts` and
 * `roster.ts` each carried their own `familyPath`, their own `newIdempotencyKey`, their own
 * spelling of the tools' shared envelope and their own `confirm*Write`. Identical, and drifting
 * was only a matter of time — which is the argument `contextNouns.ts` already won inside the
 * service and `words.ts` won inside this app.
 *
 * **Nothing about a *particular* write lives here**, and that boundary is the reason this is
 * safe to share: a tool's own envelope extensions (`RememberEnvelope.collision`,
 * `ForgetEnvelope.deleting`, `DeleteItemEnvelope.deleting`, `DisbandEnvelope.erased`) stay in
 * the module that renders them, and every propose helper stays with its screen. What is here is
 * the plumbing all twelve routes share and none of them may spell differently.
 */

import { type AppApi, appFetch } from "./client";

/**
 * The routes the panel writes through — #298's `WRITE_ROUTES`, minus the ones no screen uses.
 *
 * A union rather than a bare string, so {@link confirmWrite} cannot be handed a tail that names
 * no tool. That matters because `confirmProposal`'s `expectedTool` (#107) refuses a proposal
 * confirmed through the wrong route, and a mis-typed tail would turn *"yes, delete it"* into a
 * refusal the family cannot act on.
 *
 * **`/capture` is deliberately absent, and its absence is a product rule** (#302, #310). The
 * panel has no *＋ New* on either the memory screen or the list, because classifying an
 * utterance is the host model's job and the server calls no completions API — so a panel could
 * only create by making the family fill in a form, which is the thing this product exists not
 * to be. `/capture` is the route that would do it, so it is not in the vocabulary this app can
 * express. `send_feedback` is absent for #298's own reason: it is the only tool that sends
 * family language outside the tenant, and whether the panel offers that is a product question
 * nobody has asked yet.
 */
export type WriteTail =
  | "/resolve"
  | "/update"
  | "/remember"
  | "/correct-this"
  | "/forget-this"
  | "/delete-item"
  | "/invite-member"
  | "/revoke-invitation"
  | "/remove-member"
  | "/leave-family"
  | "/transfer-ownership"
  | "/disband-family";

/**
 * What a write answers with — the tools' shared envelope (ADR 0006 decision 5).
 *
 * `status` is the field to branch on, never the HTTP code: #298 answers `200` for both, on the
 * grounds that the envelope's own status is the structural statement and a `202` would be a
 * second, weaker one.
 */
export interface WriteEnvelope {
  readonly status: "applied" | "proposed";
  readonly needsConfirmation: boolean;
  readonly proposalId: string | null;
  /** The family-facing sentence. **The screen relays this and composes no alternative.** */
  readonly message: string;
}

export function familyPath(familyId: string, tail: string): string {
  return `/app/families/${encodeURIComponent(familyId)}${tail}`;
}

/**
 * A fresh idempotency key.
 *
 * `crypto.randomUUID()` is available in every browser this app supports and its output is
 * inside the tool contract's `[A-Za-z0-9._:-]+` (#119). It is minted **per attempt** rather
 * than per keystroke: the guard exists so a retry of *the same* write is absorbed, and a key
 * held across an edited body would make the second, different write a silent replay of the
 * first.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** One propose, on the tool's own route, carrying the tool's own input. */
export function postWrite<T extends WriteEnvelope>(
  api: AppApi,
  familyId: string,
  tail: WriteTail,
  body: Record<string, unknown>,
): Promise<T> {
  return appFetch<T>(api, familyPath(familyId, tail), { method: "POST", body });
}

/**
 * Apply a stored proposal: `{ confirm: true, proposalId }` and **nothing else**.
 *
 * The body-free confirm is ADR 0011's whole mechanism, so this function takes no payload to
 * send — a paraphrased re-send cannot change what is written because there is nowhere to put
 * one. `tail` names the tool, which is what lets `confirmProposal`'s `expectedTool` (#107)
 * refuse a `forget` proposal confirmed through `/remember`.
 */
export function confirmWrite<T extends WriteEnvelope>(
  api: AppApi,
  familyId: string,
  tail: WriteTail,
  proposalId: string,
): Promise<T> {
  return postWrite<T>(api, familyId, tail, { confirm: true, proposalId });
}
